import { toast as sonnerToast, Toaster as SonnerToaster, ToasterProps } from 'sonner';
import { AlertCircle, CheckCircle, AlertTriangle, X } from 'lucide-react';
import { cva } from 'class-variance-authority';
import { Button } from './button';
import { Card } from './card';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

export type TToastVariant = 'error' | 'success' | 'warning';
export type TToastOptions = {
  title: string;
  description?: string;
} & ToasterProps;

interface IToast {
  variant: TToastVariant;
  t: string | number;
  message?: string;
  options?: TToastOptions;
}

const toastIconVariants = cva('flex h-9 w-9 items-center justify-center rounded-full shrink-0', {
  variants: {
    variant: {
      success: 'bg-emerald-100 dark:bg-emerald-800 text-emerald-600 dark:text-emerald-200',
      warning: 'bg-amber-100 dark:bg-amber-800 text-amber-600 dark:text-amber-200',
      error: 'bg-red-100 dark:bg-red-800 text-red-600 dark:text-red-200',
    },
  },
  defaultVariants: {
    variant: 'success',
  },
});

const variantToIconMap: Record<TToastVariant, JSX.Element> = {
  success: (
    <div className={toastIconVariants({ variant: 'success' })}>
      <CheckCircle className="h-5 w-5" />
    </div>
  ),
  warning: (
    <div className={toastIconVariants({ variant: 'warning' })}>
      <AlertTriangle className="h-5 w-5" />
    </div>
  ),
  error: (
    <div className={toastIconVariants({ variant: 'error' })}>
      <AlertCircle className="h-5 w-5" />
    </div>
  ),
};

const toastVariants = cva('w-[356px] shadow-lg', {
  variants: {
    variant: {
      success: 'bg-background border-l-4 border-l-emerald-500 dark:border-l-emerald-400',
      warning: 'bg-background border-l-4 border-l-amber-500 dark:border-l-amber-400',
      error: 'bg-background border-l-4 border-l-red-500 dark:border-l-red-400',
    },
  },
  defaultVariants: {
    variant: 'success',
  },
});

const Toast: React.FC<IToast> = ({ variant, message, options, t }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
    >
      <Card className={cn('flex items-start gap-3 p-4 rounded-lg border shadow-md', toastVariants({ variant }))}>
        {variantToIconMap[variant]}

        <div className="flex-1 pt-1">
          <h4 className="font-medium text-foreground">{message || options?.title}</h4>
          {options?.description && <p className="text-sm mt-1 text-muted-foreground">{options.description}</p>}
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-full hover:bg-muted"
          onClick={() => sonnerToast.dismiss(t)}
        >
          <X className="h-4 w-4 text-muted-foreground" />
          <span className="sr-only">Close</span>
        </Button>
      </Card>
    </motion.div>
  );
};

const toast: Record<TToastVariant, (message?: string, options?: TToastOptions) => void> = {
  success: (message, options) =>
    sonnerToast.custom((t) => <Toast variant="success" message={message} options={options} t={t} />),
  error: (message, options) =>
    sonnerToast.custom((t) => <Toast variant="error" message={message} options={options} t={t} />),
  warning: (message, options) =>
    sonnerToast.custom((t) => <Toast variant="warning" message={message} options={options} t={t} />),
};

const Toaster = () => {
  return (
    <SonnerToaster
      position="bottom-right"
      expand={false}
      closeButton={false}
      offset={16}
      gap={8}
      theme="system"
      className="toaster-wrapper"
    />
  );
};

export { Toaster, toast };
